import * as i1$1 from '@ngrx/effects';
import { createEffect, ofType, EffectsModule } from '@ngrx/effects';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, APP_INITIALIZER, NgModule } from '@angular/core';
import { throwError, of } from 'rxjs';
import { catchError, concatMap, map, filter } from 'rxjs/operators';
import * as i1$2 from '@ngrx/store';
import { createAction, props, createReducer, on, StoreModule, createFeatureSelector, createSelector } from '@ngrx/store';
import * as i1 from '@angular/common/http';
import { createEntityAdapter } from '@ngrx/entity';
import * as i2 from '@panamax/app-state';

const GL_ACTIONS = {
    loadGlCodes: createAction('[NgRxGl - GlCodes] Load GL Codes Based on Customer'),
    loadGlCodesSuccess: createAction('[NgRxGl - GlCodes] GL Codes Loaded Successfully', props()),
    loadGlCodesFail: createAction('[NgRxGl - GlCodes] Load GL Codes Failed', props()),
    resetState: createAction('[NgRxGl - GlCodes] Reset Gl State'),
    downloadGlCodeCsv: createAction('[NgRxGl - GlCodes] Download Gl Code CSV'),
};

const NGRX_GL_LIB_CONFIG = new InjectionToken('NGRX_GL_LIB_CONFIG');

/* eslint-disable no-shadow */
class GeneralLedgerHttpClientService {
    constructor(config, http) {
        this.config = config;
        this.http = http;
    }
    get(path, queryParams) {
        let url = `${this.config.apiUrl}/${path}`;
        if (queryParams) {
            queryParams.forEach((val, key) => {
                url += url.includes('?') ? `&${key}=${val}` : `?${key}=${val}`;
            });
        }
        return this.http.get(url).pipe(catchError(this.handleError));
    }
    handleError(error) {
        return throwError(error);
    }
}
GeneralLedgerHttpClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: GeneralLedgerHttpClientService, deps: [{ token: NGRX_GL_LIB_CONFIG }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
GeneralLedgerHttpClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: GeneralLedgerHttpClientService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: GeneralLedgerHttpClientService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_GL_LIB_CONFIG]
                }] }, { type: i1.HttpClient }]; } });

class GeneralLedgerEffects {
    constructor(actions$, httpService) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.load$ = createEffect(() => this.actions$.pipe(ofType(GL_ACTIONS.loadGlCodes), concatMap((action) => this.httpService.get('generalledger').pipe(map((custGlCodes) => GL_ACTIONS.loadGlCodesSuccess({ custGlCodes })), catchError((error) => of(GL_ACTIONS.loadGlCodesFail({ error })))))));
    }
}
GeneralLedgerEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: GeneralLedgerEffects, deps: [{ token: i1$1.Actions }, { token: GeneralLedgerHttpClientService }], target: i0.ɵɵFactoryTarget.Injectable });
GeneralLedgerEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: GeneralLedgerEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: GeneralLedgerEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Actions }, { type: GeneralLedgerHttpClientService }]; } });

/* eslint-disable no-shadow */
const generalLedgerAdapter = createEntityAdapter({
    selectId: (gl) => gl.glId,
});
const initialGeneralLedgerState = generalLedgerAdapter.getInitialState({
    loaded: false,
    loading: false,
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _GeneralLedgerReducer = createReducer(initialGeneralLedgerState, on(GL_ACTIONS.resetState, (state, action) => initialGeneralLedgerState), on(GL_ACTIONS.loadGlCodes, (state, action) => ({
    ...state,
    loaded: false,
    loading: true,
})), on(GL_ACTIONS.loadGlCodesSuccess, (state, action) => generalLedgerAdapter.upsertMany(action.custGlCodes, {
    ...state,
    loaded: true,
    loading: false,
})), on(GL_ACTIONS.loadGlCodesFail, (state, action) => ({
    ...state,
    error: action.error,
    loaded: true,
    loading: false,
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function generalLedgerReducer(state, action) {
    return _GeneralLedgerReducer(state, action);
}

const GL_KEY = 'gl';

class GeneralLedgerService {
    constructor(store, panAppState) {
        this.store = store;
        this.panAppState = panAppState;
    }
    initialize() {
        this.panAppState.customer$
            .pipe(filter((customer) => customer?.selected))
            .subscribe((customer) => {
            this.store.dispatch(GL_ACTIONS.resetState());
            this.store.dispatch(GL_ACTIONS.loadGlCodes());
        });
    }
}
GeneralLedgerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: GeneralLedgerService, deps: [{ token: i1$2.Store }, { token: i2.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
GeneralLedgerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: GeneralLedgerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: GeneralLedgerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Store }, { type: i2.PanAppState }]; } });

class NgRxGlModule {
    constructor(ngrxGlLibConfig, store) {
        this.ngrxGlLibConfig = ngrxGlLibConfig;
        this.store = store;
    }
    static forRoot(ngRxGlLibConfig, store) {
        return {
            ngModule: NgRxGlModule,
            providers: [
                {
                    provide: NGRX_GL_LIB_CONFIG,
                    useValue: ngRxGlLibConfig,
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializeGeneralLedgerState,
                    deps: [GeneralLedgerService],
                    multi: true,
                },
            ],
        };
    }
}
NgRxGlModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgRxGlModule, deps: [{ token: NGRX_GL_LIB_CONFIG }, { token: i1$2.Store }], target: i0.ɵɵFactoryTarget.NgModule });
NgRxGlModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: NgRxGlModule, imports: [i1$2.StoreFeatureModule, i1$1.EffectsFeatureModule] });
NgRxGlModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgRxGlModule, providers: [GeneralLedgerHttpClientService, GeneralLedgerEffects], imports: [StoreModule.forFeature(GL_KEY, generalLedgerReducer),
        EffectsModule.forFeature([GeneralLedgerEffects])] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgRxGlModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [
                        StoreModule.forFeature(GL_KEY, generalLedgerReducer),
                        EffectsModule.forFeature([GeneralLedgerEffects]),
                    ],
                    providers: [GeneralLedgerHttpClientService, GeneralLedgerEffects],
                    exports: [],
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_GL_LIB_CONFIG]
                }] }, { type: i1$2.Store }]; } });
const initializeGeneralLedgerState = (generalLedgerService) => {
    const state = () => generalLedgerService.initialize();
    return state;
};

const selectGeneralLedgerState = createFeatureSelector(GL_KEY);
const selectAllGlCodes = () => createSelector(selectGeneralLedgerState, (glCodes) => Object.values(glCodes.entities));
const selectGlPimDictionary = createSelector(selectGeneralLedgerState, (glCodeState) => {
    const pimAssignmentDictionary = {};
    glCodeState?.ids?.forEach((glId) => {
        const generalLedger = glCodeState?.entities[glId];
        generalLedger?.pimAssignments?.forEach((pim) => {
            pimAssignmentDictionary[pim.pimId] = {
                pimId: pim.pimId,
                pimType: pim.pimType,
                glCode: generalLedger.glCode,
                glDescription: generalLedger.glDescription,
            };
        });
    });
    return pimAssignmentDictionary;
});

/*
 * Public API Surface of ngrx-gl
 */

/**
 * Generated bundle index. Do not edit.
 */

export { GL_ACTIONS, NGRX_GL_LIB_CONFIG, NgRxGlModule, selectAllGlCodes, selectGeneralLedgerState, selectGlPimDictionary };
